import { Component, OnInit, SecurityContext, Inject, Optional } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ShareInviteModalComponent } from '../share-invite-modal/share-invite-modal.component';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { EventService } from 'src/app/core/services/event.service';
import { EventTypes } from 'src/app/core/models/events.model';

@Component({
  selector: 'app-guide-completed-modal',
  templateUrl: './guide-completed-modal.component.html',
  styleUrls: ['./guide-completed-modal.component.scss']
})
export class GuideCompletedModalComponent implements OnInit {
  modalTitle: string = '';
  modalText: SafeHtml;
  buttonLabel: string = '';
  isLoading: boolean = true;

  private currentLang: string = 'pt';

  constructor(
    public dialogRef: MatDialogRef<GuideCompletedModalComponent>,
    private dialog: MatDialog,
    private claimsService: ClaimsService,
    private eventService: EventService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    @Optional() @Inject(MAT_DIALOG_DATA) private data?: {
      guideCompletedConfig?: any;
      caseId?: string;
      claimId?: string;
    }
  ) {
    this.currentLang = this.translate.currentLang || this.translate.defaultLang || 'pt';
    this.modalText = this.sanitizer.bypassSecurityTrustHtml('');
  }

  async ngOnInit(): Promise<void> {
    try {
      // Se os dados já foram passados, usa diretamente (otimização)
      if (this.data?.guideCompletedConfig) {
        this.loadConfigFromData(this.data.guideCompletedConfig);
        await this.sendPageLoadEvent();
      } else {
        // Caso contrário, carrega normalmente
        await Promise.all([
          this.loadConfig(),
          this.sendPageLoadEvent()
        ]);
      }
    } finally {
      this.isLoading = false;
    }
  }

  private loadConfigFromData(guideCompletedConfig: any): void {
    if (!guideCompletedConfig || guideCompletedConfig.enabled === false) {
      this.close();
      return;
    }

    this.modalTitle = guideCompletedConfig.title !== undefined
      ? this.getTranslatedText(guideCompletedConfig.title)
      : this.translate.instant('RegistrationCompleted');

    const textHtml = guideCompletedConfig.text !== undefined
      ? this.getTranslatedText(guideCompletedConfig.text)
      : this.translate.instant('CompletedGuideMessage');

    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, textHtml) || '';
    this.modalText = this.sanitizer.bypassSecurityTrustHtml(sanitized);

    this.buttonLabel = guideCompletedConfig.buttonLabel !== undefined
      ? this.getTranslatedText(guideCompletedConfig.buttonLabel)
      : 'OK';
  }

  private async loadConfig(): Promise<void> {
    try {
      const claims = await this.claimsService.getMyClaimsWithCase(1, 0);

      if (!claims?.length) {
        this.close();
        return;
      }

      const { data } = await this.claimsService.getCaseById(claims[0].case_id);
      const config: any = data?.configuration || {};
      const guideCompletedConfig = config?.guide_completed_modal;

      if (!guideCompletedConfig || guideCompletedConfig.enabled === false) {
        this.close();
        return;
      }

      this.loadConfigFromData(guideCompletedConfig);
    } catch (error) {
      console.error('Error loading guide completed modal config:', error);
      this.close();
    }
  }

  private getTranslatedText(textObj: any): string {
    if (!textObj) return '';

    if (typeof textObj === 'string') {
      return textObj;
    }

    return textObj[this.currentLang] || textObj['pt'] || textObj['en'] || '';
  }

  private async sendPageLoadEvent(): Promise<void> {
    try {
      // Usa os dados passados se disponíveis, senão busca
      let claimId = this.data?.claimId;
      let caseId = this.data?.caseId;

      if (!claimId || !caseId) {
        const claims = await this.claimsService.getMyClaimsWithCase(1, 0);
        
        if (!claims?.length) {
          console.warn('No claims found for event tracking');
          return;
        }

        claimId = claims[0].id;
        caseId = claims[0].case_id;
      }
      
      await this.eventService.sendEventWithCaseId(
        EventTypes.SIGN_IN_CONFIRMATION_LOAD_PAGE,
        claimId,
        { caseId }
      );
    } catch (error) {
      console.error('Error sending page load event:', error);
    }
  }

  close(): void {
    this.dialogRef.close();
    this.checkAndOpenShareModal();
  }

  private async checkAndOpenShareModal(): Promise<void> {
    try {
      const claims = await this.claimsService.getMyClaimsWithCase(1, 0);
      
      if (!claims?.length) {
        return;
      }

      const { data } = await this.claimsService.getCaseById(claims[0].case_id);
      const config: any = data?.attributes || {};
      
      if (config?.sharing) {
        this.openShareInviteModal();
      }
    } catch (error) {
      console.error('Error checking sharing config:', error);
    }
  }

  private openShareInviteModal(): void {
    this.dialog.open(ShareInviteModalComponent, {
      width: '560px',
      maxWidth: '95vw',
      disableClose: false,
      panelClass: 'share-invite-modal-container'
    });
  }
}
